import React from "react";
import { useTranslation } from "react-i18next";

export interface UserListModalUser {
  socketId: string;
  defaultNickName: string;
  nickName: string;
  id: number;
  isHost: boolean;
  state: string;
  isAI: boolean;
}

interface UserListModalProps {
  userList: UserListModalUser[];
  isOpen: boolean;
  onClose: () => void;
  variant?: "embedded" | "standalone";
  className?: string;
}

const UserListModal: React.FC<UserListModalProps> = ({
  userList,
  isOpen,
  onClose,
  variant = "embedded",
  className = "",
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  if (variant === "standalone") {
    return (
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="bg-gray-800 rounded-xl w-full max-w-md h-[500px] flex flex-col border border-gray-700 shadow-2xl">
          <div className="px-4 py-3 bg-gray-700 border-b border-gray-600 rounded-t-xl">
            <h2 className="text-lg font-medium text-white flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-users mr-2"></i>
                {t("onlineUsers")}
                <span className="ml-2 bg-green-600 text-xs text-white px-2 py-1 rounded-full">
                  {userList.length}
                </span>
              </div>
              <button
                onClick={onClose}
                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
              >
                ✕
              </button>
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {userList.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-2 p-2 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <div
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      user.isHost ? "bg-yellow-500" : "bg-green-500"
                    }`}
                  ></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium text-sm truncate">{user.nickName}</div>
                    <div className="text-xs text-gray-400">
                      {user.isHost ? "호스트" : "참가자"}
                      {user.isAI && " (AI)"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // embedded variant (for use inside chatbox)
  return (
    <div
      className={`md:relative md:left-0 md:right-0 md:w-90 md:h-full md:max-h-full ${
        isOpen
          ? "left-2 right-2 shadow-xl bg-gray-800"
          : "right-[-100%]"
      } absolute flex flex-col max-h-[60vh] md:mt-0 md:rounded-none mt-1 bg-gray-800 rounded-xl overflow-hidden shadow-2xl border border-gray-700 transform transition-all duration-300 ease-in-out z-40 ${className}`}
    >
      <div className="px-4 py-2 bg-gray-700 border-b border-gray-600">
        <h2 className="md:text-lg md:font-medium text-white flex items-center justify-between">
          <div>
            <i className="fas fa-users mr-2"></i>
            {t("onlineUsers")}
            <span className="ml-2 bg-green-700 text-xs text-white px-2 py-1 rounded-full">
              {userList.length}
            </span>
          </div>
          <button
            className="md:hidden px-1"
            onClick={onClose}
          >
            <i className="fas fa-arrow-right"></i>
          </button>
        </h2>
      </div>
      <div className="p-1 overflow-y-scroll grid grid-cols-2 md:grid-cols-4 gap-1">
        {userList.map((user, index) => (
          <div
            key={user.id}
            className="flex items-center gap-2 p-2 m-1 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            <div
              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                user.isHost ? "bg-yellow-500" : "bg-green-500"
              }`}
            ></div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-xs font-medium truncate">
                {user.nickName}
              </div>
              <div className="text-xs text-gray-400">
                {user.isHost ? "호스트" : "참가자"}
                {user.isAI && " (AI)"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserListModal;